// FRONT-1698: pure logic behind the composer model picker (the model button,
// the model window, the thinking pill's gate). No React, no network — everything the
// components decide that can be wrong lives here so it is unit-testable.

import type { Region } from '~/components/model-badges';

/** Mirrors GET /api/llm/model-picker (src/llm/model-picker-routes.ts). */
export interface PickerModelEntry {
  id: string;
  displayName: string;
  provider?: string;
  logo?: string;
  region?: Region;
  /** The model takes a thinking depth (the slider has an effect on it). */
  reasoning?: boolean;
  /** The model has Very high and Pro as tiers of its own; otherwise it thinks at High there. */
  topTiers?: boolean;
}

export interface PickerPrefs {
  enabled: boolean;
  models: PickerModelEntry[];
  /** Starred model ids (the Favourites section). Absent from an older server: none. */
  starred?: string[];
  lastModel: string | null;
  defaultModel: { id: string; displayName: string; reasoning?: boolean; topTiers?: boolean } | null;
}

/** Mirrors GET /api/llm/chat-model/:sessionId. */
export interface ChatModelState {
  model: string | null;
  active: { id: string; displayName: string; source: 'pick' | 'default' } | null;
}

/**
 * Drop-reorder over the FULL id list. `targetId === null` appends. Dropping a
 * row on itself (or an unknown dragged id) returns the same reference.
 */
export function reorderIds(order: string[], draggedId: string, targetId: string | null): string[] {
  if (targetId === draggedId || !order.includes(draggedId)) return order;
  const without = order.filter((id) => id !== draggedId);
  if (targetId == null) return [...without, draggedId];
  const index = without.indexOf(targetId);
  const insertAt = index === -1 ? without.length : index;
  return [...without.slice(0, insertAt), draggedId, ...without.slice(insertAt)];
}

/** The star toggled for one model: added at the end, or removed. Never mutates. */
export function toggleStar(starred: readonly string[], id: string): string[] {
  return starred.includes(id) ? starred.filter((s) => s !== id) : [...starred, id];
}

/**
 * The list split into the Favourites section (starred) and the rest, each in
 * the person's list order. A model appears in exactly one of the two.
 */
export function splitFavourites(
  models: readonly PickerModelEntry[],
  starred: readonly string[] | undefined,
): { favourites: PickerModelEntry[]; others: PickerModelEntry[] } {
  const set = new Set(starred ?? []);
  return {
    favourites: models.filter((m) => set.has(m.id)),
    others: models.filter((m) => !set.has(m.id)),
  };
}

/**
 * The drop target (for {@link reorderIds}) that puts `draggedId` in last place
 * among the favourites: before whatever follows the last OTHER favourite in the
 * full order — favourites can sit anywhere in it, the sections are a filter
 * over one list. Null appends.
 */
export function favouritesEndTarget(order: readonly string[], starred: ReadonlySet<string>, draggedId: string): string | null {
  const rest = order.filter((id) => id !== draggedId);
  const lastFav = rest.reduce((at, id, i) => (starred.has(id) ? i : at), -1);
  return rest[lastFav + 1] ?? null;
}

/** Rows to show for a search term; saved order is preserved. Blank = all. */
export function filterModels(
  models: readonly PickerModelEntry[],
  search: string,
  matches: (haystack: string, search: string) => number,
): PickerModelEntry[] {
  if (!search.trim()) return [...models];
  return models.filter((m) => matches(`${m.displayName} ${m.id} ${m.provider ?? ''}`, search) > 0);
}

export function modelName(prefs: PickerPrefs | undefined, id: string | null): string | null {
  if (!id) return null;
  const hit = prefs?.models.find((m) => m.id === id);
  if (hit) return hit.displayName;
  if (prefs?.defaultModel?.id === id) return prefs.defaultModel.displayName;
  return null;
}

// Leading words that name WHERE a model is hosted or WHO made it, not which
// model it is. Stripped from the front of a name only, so a new model from a
// known host/maker shortens with no change here; an unknown prefix just shows
// in full. `ai` counts only straight after `azure` ("azure-ai-gpt-6-sol").
const HOST_WORDS = new Set(['bedrock', 'google', 'vertex', 'azure', 'nebius', 'aws', 'gcp']);
const MAKER_WORDS = new Set(['anthropic', 'claude', 'openai', 'meta']);
const UPPER_WORDS = new Set(['gpt', 'ai']);
// Trailing hosting jargon ("model as a service") that says nothing about the model.
const TAIL_WORDS = new Set(['maas']);

function titleWord(word: string): string {
  if (UPPER_WORDS.has(word)) return word.toUpperCase();
  // Sizes and versions: 26b → 26B, v3.2 → V3.2, k3 → K3.
  if (/^\d+(\.\d+)?[bkm]$/.test(word) || /^[a-z]\d/.test(word)) return word.toUpperCase();
  return word.charAt(0).toUpperCase() + word.slice(1);
}

/**
 * The short, human name for the button and model lists: host and maker words go,
 * the rest is title-cased — "bedrock-anthropic-claude-opus-5.5" → "Opus 5.5",
 * "azure-ai-gpt-61-sol" → "GPT-6.1 Sol". The window still shows the full name.
 */
export function shortModelName(name: string): string {
  const words = name
    .replace(/\([^)]*\)/g, ' ')
    .toLowerCase()
    .split(/[\s_/-]+/)
    .filter(Boolean);
  let i = 0;
  while (i < words.length - 1) {
    const w = words[i]!;
    if (HOST_WORDS.has(w) || MAKER_WORDS.has(w) || (w === 'ai' && words[i - 1] === 'azure')) i++;
    else break;
  }
  let end = words.length;
  while (end > i + 1 && TAIL_WORDS.has(words[end - 1]!)) end--;
  const rest = words.slice(i, end);
  if (rest.length === 0) return name;
  const out: string[] = [];
  for (let j = 0; j < rest.length; j++) {
    const w = rest[j]!;
    const next = rest[j + 1];
    if (w === 'gpt' && next && /^\d/.test(next)) {
      // Azure deployment names drop the dot: gpt-55 → GPT-5.5.
      const version = /^\d\d$/.test(next) ? `${next[0]}.${next[1]}` : next;
      out.push(`GPT-${version}`);
      j++;
    } else {
      out.push(titleWord(w));
    }
  }
  return out.join(' ');
}

export interface ActiveModelInputs {
  sessionId: string | undefined;
  sessionState: ChatModelState | null | undefined;
  /** GET /api/llm/chat-model/default → the standing pick (lastModel). */
  standingModel: string | null | undefined;
  prefs: PickerPrefs | undefined;
}

export interface ActiveModel {
  /** The model the next turn will use; null while nothing has loaded. */
  id: string | null;
  name: string | null;
  /** The id of an explicit pick (the window's highlighted row), else null. */
  pickId: string | null;
}

/**
 * What the button shows. With a session the server has already resolved it.
 * With none: standing pick (lastModel) if set, else the default model a fresh
 * chat would resolve to.
 */
export function resolveActiveModel(inputs: ActiveModelInputs): ActiveModel {
  const { sessionId, sessionState, standingModel, prefs } = inputs;
  if (sessionId && sessionState?.active) {
    const a = sessionState.active;
    return { id: a.id, name: a.displayName, pickId: a.source === 'pick' ? a.id : null };
  }
  const standing = standingModel ?? prefs?.lastModel ?? null;
  if (standing) {
    return { id: standing, name: modelName(prefs, standing) ?? standing, pickId: standing };
  }
  const d = prefs?.defaultModel ?? null;
  return { id: d?.id ?? null, name: d?.displayName ?? null, pickId: null };
}

/** Whether the model the next turn uses takes a thinking depth. Unknown reads as yes. */
export function activeModelReasons(prefs: PickerPrefs | undefined, id: string | null): boolean {
  if (!prefs || !id) return true;
  const hit = prefs.models.find((m) => m.id === id);
  if (hit) return hit.reasoning !== false;
  if (prefs.defaultModel?.id === id) return prefs.defaultModel.reasoning !== false;
  return true;
}

/** Whether the model the next turn uses has Very high and Pro of its own. Unknown reads as yes. */
export function activeModelTopTiers(prefs: PickerPrefs | undefined, id: string | null): boolean {
  if (!prefs || !id) return true;
  const hit = prefs.models.find((m) => m.id === id) ?? (prefs.defaultModel?.id === id ? prefs.defaultModel : undefined);
  return hit?.topTiers !== false;
}

/**
 * Whether a slider reason is one the person has to see and act on (shown as
 * visible text on touch screens), rather than a passing state.
 */
export function isLastingThinkingReason(inputs: { busy: boolean; loaded: boolean }): boolean {
  return !inputs.busy && inputs.loaded;
}

/**
 * Why the thinking pill (and the slider it opens) cannot be used right now, or null when it can — the
 * one source for its `disabled` and the explanation the person sees.
 */
export function thinkingControlReason(inputs: {
  busy: boolean;
  loaded: boolean;
  failed: boolean;
  modelReasons: boolean;
}): string | null {
  if (!inputs.loaded) {
    return inputs.failed ? "Couldn't load your thinking setting. Reload the page to try again." : 'Loading your thinking setting…';
  }
  if (inputs.busy) return "Thinking can't be changed while EDDY is answering.";
  if (!inputs.modelReasons) return "This model doesn't think before answering, so there is nothing to adjust.";
  return null;
}

/** The accessible name of the model button. */
export function modelButtonLabel(name: string | null): string {
  return `Model: ${name ?? 'loading'}`;
}

export interface ControlGateInputs {
  /** The composer is mid-answer. */
  busy: boolean;
  /** The picker prefs are in the cache. */
  prefsLoaded: boolean;
  /** The prefs request failed (and nothing is cached). */
  prefsFailed: boolean;
}

/**
 * Why the model button cannot be used right now, or null when it
 * can. The ONE source for both `disabled` and the explanation shown to the
 * person: a control is disabled iff this returns a sentence.
 */
export function modelControlReason({ busy, prefsLoaded, prefsFailed }: ControlGateInputs): string | null {
  if (!prefsLoaded) {
    return prefsFailed ? "Couldn't load your models. Reload the page to try again." : 'Loading your models…';
  }
  if (busy) return "The model can't be changed while EDDY is answering.";
  return null;
}

/** Optimistic prefs patch, applied to the cache before the PUT lands. */
export function applyPrefsPatch(prefs: PickerPrefs, patch: { order?: string[]; starred?: string[] }): PickerPrefs {
  let models = prefs.models;
  if (patch.order) {
    const byId = new Map(prefs.models.map((m) => [m.id, m]));
    const ordered = patch.order.map((id) => byId.get(id)).filter((m): m is PickerModelEntry => !!m);
    const seen = new Set(ordered.map((m) => m.id));
    models = [...ordered, ...prefs.models.filter((m) => !seen.has(m.id))];
  }
  return { ...prefs, models, ...(patch.starred ? { starred: patch.starred } : {}) };
}
